/**
 * Webmail three-pane composites (DESIGN.md T10): FolderTree, MailListPane, ReadingPane.
 * They are data-agnostic: the app maps its API model to these small view models and passes
 * localized labels as props. Actions (context menu, "…" buttons) come from the app's action
 * registry as `ContextMenuSpec`s (DESIGN.md §18).
 */
import { useVirtualizer } from '@tanstack/react-virtual';
import { ChevronRightIcon, FlagIcon, FolderIcon, ImageOffIcon, LoaderCircleIcon, PaperclipIcon, type LucideIcon } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent, type ReactNode } from 'react';
import { ActionOverflowMenu } from '../actions/action-menu';
import { ContextMenuArea, type ContextMenuSpec } from '../actions/context-menu-area';
import { contextTargets, nextSelection } from '../actions/registry';
import { Button } from '../components/button';
import { Alert, Skeleton } from '../components/misc';
import { PersonAvatar, PersonName } from './person';
import { cn } from '../lib/utils';
import { EmptyState, ErrorState } from '../composites/states';
import { buildMailDocument, sanitizeMailHtml } from './sanitize';
import { useMailTheme } from './use-mail-theme';

/* ---------------------------------------------------------------- FolderTree */

export interface FolderTreeItem {
  id: string;
  /** Exact identity from discovery; aliases never imply permission or another store. */
  nativeId?: string | undefined;
  name: string;
  /** Full accessible identity or store detail; the visible label remains concise. */
  description?: string | undefined;
  icon?: LucideIcon;
  unread: number;
  /** Nesting level, 0 = top. */
  depth?: number;
  /** False for structural rows such as a mailbox group; those rows cannot navigate to a folder. */
  selectable?: boolean;
  /** Every mailbox uses the same structural root row, regardless of ownership or store type. */
  mailboxRoot?: boolean;
  /** Explicit parent, never inferred across mailbox scopes. */
  parentId?: string | undefined;
}

export interface FolderTreeProps {
  label: string;
  folders: readonly FolderTreeItem[];
  activeId?: string;
  onSelect: (id: string) => void;
  /** Accessible text for the unread count, contains `{count}`. */
  unreadLabel: string;
  header?: ReactNode;
  /**
   * Folder actions (DESIGN.md §18): right-click, Shift+F10 and long-press open them; a "…" button
   * per folder (on hover/focus, always on touch screens) is the path without right-click.
   */
  folderMenu?: (id: string) => ContextMenuSpec | undefined;
  /** Accessible name of the "…" button, contains `{name}` (default "More actions"). */
  folderActionsLabel?: string;
  collapsedIds?: ReadonlySet<string>;
  onToggleCollapsed?: (id: string) => void;
  /** Localized accessible toggle name containing `{name}`. */
  collapseLabel?: string;
  expandLabel?: string;
  /** The app authorizes discovered destinations for its current internal drag. */
  canDropMailItem?: (target: string) => boolean;
  onDropMailItem?: (target: string, item: DraggedMailItem, mode: 'move' | 'copy') => void;
}

const depthIndent = ['pl-2', 'pl-6', 'pl-10', 'pl-14'] as const;
const treeRowIndent = ['pl-7', 'pl-11', 'pl-15', 'pl-19'] as const;
const treeToggleIndent = ['left-1', 'left-5', 'left-9', 'left-13'] as const;

/** Left padding of a folder row for its tree depth (tree, picker). */
export const folderIndent = (depth = 0): string => depthIndent[Math.min(depth, depthIndent.length - 1)]!;

export function FolderTree({ label, folders, activeId, onSelect, unreadLabel, header, folderMenu, folderActionsLabel, collapsedIds, onToggleCollapsed, collapseLabel, expandLabel, canDropMailItem, onDropMailItem }: FolderTreeProps) {
  const [dropTarget, setDropTarget] = useState<string>();
  const byId = new Map(folders.map((folder) => [folder.id, folder]));
  const parents = new Set(folders.flatMap((folder) => folder.parentId ? [folder.parentId] : []));
  const visible = folders.filter((folder) => {
    const seen = new Set<string>([folder.id]);
    let parent = folder.parentId;
    while (parent && !seen.has(parent)) {
      if (collapsedIds?.has(parent)) return false;
      seen.add(parent);
      parent = byId.get(parent)?.parentId;
    }
    return true;
  });
  const list = (
    <ul aria-label={label} className="flex flex-col gap-0.5">
      {visible.map((folder) => {
        const Icon = folder.icon ?? FolderIcon;
        const active = folder.id === activeId;
        const menu = folderMenu?.(folder.id);
        return (
          <li key={folder.id} className={cn('group/folder relative', folder.mailboxRoot && 'mt-4 first:mt-0')}>
            {folder.mailboxRoot ? <MailboxRootRow folder={folder} expanded={!collapsedIds?.has(folder.id)}
              onToggle={() => onToggleCollapsed?.(folder.id)}
              toggleLabel={(collapsedIds?.has(folder.id) ? expandLabel : collapseLabel)?.replace('{name}', folder.name)} /> : <>
            {onToggleCollapsed && parents.has(folder.id) && (
              <button type="button" aria-expanded={!collapsedIds?.has(folder.id)}
                aria-label={(collapsedIds?.has(folder.id) ? expandLabel : collapseLabel)?.replace('{name}', folder.name)}
                onClick={() => onToggleCollapsed(folder.id)}
                className={cn('absolute top-1/2 z-10 flex size-5 -translate-y-1/2 items-center justify-center rounded outline-none hover:bg-sidebar-accent focus-visible:ring-2 focus-visible:ring-ring', treeToggleIndent[Math.min(folder.depth ?? 0, treeToggleIndent.length - 1)])}>
                <ChevronRightIcon aria-hidden className={cn('size-3', !collapsedIds?.has(folder.id) && 'rotate-90')} />
              </button>
            )}
            <button
              type="button"
              disabled={folder.selectable === false}
              title={folder.description ?? folder.name}
              aria-label={folder.description ? `${folder.name} (${folder.description})` : undefined}
              aria-current={active ? 'true' : undefined}
              onClick={() => onSelect(folder.id)}
              data-mail-drop-target={dropTarget === folder.id ? 'true' : undefined}
              onDragOver={(event) => {
                if (!onDropMailItem || folder.selectable === false || !event.dataTransfer.types.includes(MAIL_ITEM_DRAG_TYPE) || !canDropMailItem?.(folder.id)) return;
                event.preventDefault();
                event.dataTransfer.dropEffect = event.ctrlKey || event.metaKey ? 'copy' : 'move';
                setDropTarget(folder.id);
              }}
              onDragLeave={() => setDropTarget(undefined)}
              onDrop={(event) => {
                setDropTarget(undefined);
                if (!onDropMailItem || folder.selectable === false || !event.dataTransfer.types.includes(MAIL_ITEM_DRAG_TYPE) || !canDropMailItem?.(folder.id)) return;
                event.preventDefault();
                const item = readDraggedMailItem(event.dataTransfer);
                if (item) onDropMailItem(folder.id, item, event.ctrlKey || event.metaKey ? 'copy' : 'move');
              }}
              data-context-id={folderMenu ? folder.id : undefined}
              className={cn(
                'flex h-control w-full touch-callout-none items-center gap-2 rounded-md pr-2 text-sm outline-none transition-colors hover:bg-sidebar-accent focus-visible:ring-3 focus-visible:ring-sidebar-ring/50',
                onToggleCollapsed ? treeRowIndent[Math.min(folder.depth ?? 0, treeRowIndent.length - 1)] : folderIndent(folder.depth),
                active && 'bg-sidebar-primary/10 font-medium text-sidebar-primary',
                menu && 'pr-9',
                dropTarget === folder.id && 'bg-sidebar-accent ring-2 ring-sidebar-ring',
              )}
            >
              <Icon className="size-4 shrink-0" />
              <span className="flex-1 truncate text-left">{folder.name}</span>
              {folder.unread > 0 && (
                <span className="text-xs font-semibold text-primary" aria-label={unreadLabel.replace('{count}', String(folder.unread))}>
                  {folder.unread}
                </span>
              )}
            </button>
            </>}
            {menu && (
              <ActionOverflowMenu
                actions={menu.actions}
                title={menu.title}
                label={folderActionsLabel?.replace('{name}', folder.name)}
                size="icon-sm"
                align="start"
                className="absolute top-1/2 right-1 -translate-y-1/2 opacity-0 group-hover/folder:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100 pointer-coarse:opacity-100"
              />
            )}
          </li>
        );
      })}
    </ul>
  );
  return (
    <div className="flex flex-col gap-1 p-2">
      {header}
      <p className="px-2 pt-2 text-xs font-medium text-muted-foreground">{label}</p>
      <ContextMenuArea menuFor={folderMenu}>{list}</ContextMenuArea>
    </div>
  );
}

/** One mailbox header for own, mapped personal and shared stores. */
function MailboxRootRow({ folder, expanded, onToggle, toggleLabel }: { folder: FolderTreeItem; expanded: boolean; onToggle: () => void; toggleLabel?: string | undefined }) {
  const Icon = folder.icon ?? FolderIcon;
  return <button type="button" aria-expanded={expanded} aria-label={toggleLabel ?? folder.name}
    aria-description={folder.description} title={folder.description ?? folder.name} onClick={onToggle}
    className="flex h-control w-full items-center gap-2 rounded-md px-2 text-sm font-semibold outline-none hover:bg-sidebar-accent focus-visible:ring-3 focus-visible:ring-sidebar-ring/50">
    <ChevronRightIcon aria-hidden className={cn('size-3 shrink-0', expanded && 'rotate-90')} />
    <Icon aria-hidden className="size-4 shrink-0" />
    <span className="min-w-0 flex-1 truncate text-left">{folder.name}</span>
  </button>;
}

/* ---------------------------------------------------------------- MailListPane */

export interface MailListItem {
  id: string;
  from: string;
  /** Sender address, shown on hover (the list shows the display name). */
  fromAddress?: string | undefined;
  subject: string;
  preview: string;
  /** Already formatted for the user's locale. */
  dateLabel: string;
  unread: boolean;
  flagged: boolean;
  hasAttachments: boolean;
}

export interface MailListLabels {
  list: string;
  emptyTitle: string;
  emptyDescription: string;
  noSubject: string;
  hasAttachments: string;
  flagged: string;
  unread: string;
  /** Footer while the next page loads. */
  loadingMore?: string;
}

/** Load the next page when the last rendered row is this close to the end. */
const END_REACHED_THRESHOLD = 10;

export interface MailListPaneProps {
  items: readonly MailListItem[];
  activeId?: string;
  onSelect: (id: string) => void;
  /** Server-paged lists: called when the user scrolls near the end and more items exist. */
  onEndReached?: () => void;
  /** True while the next page is loading (shows a footer row, suppresses onEndReached). */
  loadingMore?: boolean;
  loading?: boolean;
  error?: boolean;
  onRetry?: () => void;
  labels: MailListLabels;
  toolbar?: ReactNode;
  className?: string;
  /**
   * Multi-selection (DESIGN.md §18): Ctrl/⌘+click toggles a message, Shift+click and Shift+↑/↓
   * select the range from `activeId`. Without `onSelectionChange` the list is single-select.
   */
  selectedIds?: readonly string[];
  onSelectionChange?: (ids: string[]) => void;
  /**
   * Message actions for right-click, Shift+F10 and long-press. Gets the targets after the selection
   * rule (`contextTargets`): the selection when the row is selected, else the row (then selected).
   */
  contextMenu?: (ids: readonly string[]) => ContextMenuSpec | undefined;
  /** Rows can be dragged (onto the composer to attach the message, DESIGN.md T11). */
  draggableRows?: boolean;
  onDragMailItem?: (item: DraggedMailItem | undefined) => void;
  /** Marks the matched search words in subjects and previews (search results only). */
  highlight?: MailHighlighter;
}

/** Marks matched search words; supplied by the app, only while a message is shown from search results. */
export interface MailHighlighter {
  /** Plain text of a subject line or a preview, as nodes with `<mark>` around the matches. */
  text: (text: string, place: 'subject' | 'body') => ReactNode;
  /** Sanitized message HTML with the matches wrapped in `<mark>`. */
  html: (html: string) => string;
}

/** Drag-and-drop type of a mail list row; data = JSON `DraggedMailItem`. */
export const MAIL_ITEM_DRAG_TYPE = 'application/x-platform-mail-item';

export interface DraggedMailItem {
  id: string;
  subject: string;
}

/** The mail item carried by a drag, if any. */
export function readDraggedMailItem(data: Pick<DataTransfer, 'getData'>): DraggedMailItem | undefined {
  try {
    const parsed = JSON.parse(data.getData(MAIL_ITEM_DRAG_TYPE)) as Partial<DraggedMailItem>;
    return typeof parsed.id === 'string' ? { id: parsed.id, subject: typeof parsed.subject === 'string' ? parsed.subject : '' } : undefined;
  } catch {
    return undefined;
  }
}

/** Virtualized message list (TanStack Virtual); rows are measured, so density just works. */
export function MailListPane({
  items,
  activeId,
  onSelect,
  onEndReached,
  loadingMore = false,
  loading,
  error,
  onRetry,
  labels,
  toolbar,
  className,
  draggableRows = false,
  onDragMailItem,
  highlight,
  selectedIds,
  onSelectionChange,
  contextMenu,
}: MailListPaneProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  // eslint-disable-next-line react-hooks/incompatible-library -- TanStack Virtual is not compiler-safe; this component is not memoized
  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 64,
    overscan: 8,
    getItemKey: (index) => items[index]?.id ?? index,
  });
  const activeIndex = useMemo(() => items.findIndex((item) => item.id === activeId), [items, activeId]);
  const virtualRows = virtualizer.getVirtualItems();
  const lastRendered = virtualRows[virtualRows.length - 1]?.index ?? -1;

  // Infinite scroll: ask for the next page once the rendered window nears the end.
  useEffect(() => {
    if (onEndReached && !loadingMore && items.length > 0 && lastRendered >= items.length - END_REACHED_THRESHOLD) onEndReached();
  }, [onEndReached, loadingMore, items.length, lastRendered]);

  const order = useMemo(() => items.map((item) => item.id), [items]);
  const selected = useMemo(() => selectedIds ?? (activeId ? [activeId] : []), [selectedIds, activeId]);
  /** Moving end of a Shift+arrow range (the anchor is `activeId`). */
  const lead = useRef<string>(undefined);

  const move = (delta: number, extend: boolean) => {
    const from = extend && lead.current && order.includes(lead.current) ? order.indexOf(lead.current) : activeIndex;
    const next = items[Math.max(0, Math.min(items.length - 1, (from < 0 ? -1 : from) + delta))];
    if (!next) return;
    if (extend && onSelectionChange && activeId) {
      lead.current = next.id;
      onSelectionChange(nextSelection(order, selected, activeId, next.id, { range: true }));
    } else {
      lead.current = undefined;
      onSelect(next.id);
    }
    virtualizer.scrollToIndex(order.indexOf(next.id), { align: 'auto' });
  };

  const choose = (id: string, event: ReactMouseEvent) => {
    const toggle = event.ctrlKey || event.metaKey;
    lead.current = undefined;
    if (onSelectionChange && (toggle || event.shiftKey)) onSelectionChange(nextSelection(order, selected, activeId, id, { toggle, range: event.shiftKey }));
    else onSelect(id);
  };

  const menuFor = (id: string) => {
    if (!contextMenu) return undefined;
    const targets = contextTargets(selected, id);
    if (targets.changed) onSelectionChange?.(targets.ids);
    return contextMenu(targets.ids);
  };

  return (
    <section aria-label={labels.list} className={cn('flex min-h-0 flex-col', className)}>
      {toolbar && <div className="flex h-bar shrink-0 items-center gap-2 border-b px-2">{toolbar}</div>}
      {error ? (
        <ErrorState onRetry={onRetry} />
      ) : loading && items.length === 0 ? (
        <div className="flex flex-col gap-3 p-3">
          {Array.from({ length: 8 }, (_, index) => (
            <div key={index} className="flex flex-col gap-1.5">
              <Skeleton className="h-3 w-1/3" />
              <Skeleton className="h-3 w-3/4" />
            </div>
          ))}
        </div>
      ) : items.length === 0 ? (
        <EmptyState title={labels.emptyTitle} description={labels.emptyDescription} />
      ) : (
        <ContextMenuArea menuFor={contextMenu && menuFor} focusedId={() => lead.current ?? activeId}>
          <div
            ref={scrollRef}
            role="listbox"
            aria-label={labels.list}
            aria-multiselectable={onSelectionChange ? true : undefined}
            aria-activedescendant={activeId ? `mail-${activeId}` : undefined}
            tabIndex={0}
            onKeyDown={(event) => {
              // Arrow keys move the selection (DESIGN.md §11.10, Outlook model).
              if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault();
                move(event.key === 'ArrowDown' ? 1 : -1, event.shiftKey);
              }
            }}
            className="min-h-0 flex-1 overflow-y-auto outline-none focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:ring-inset"
          >
            <div className="relative w-full" style={{ height: virtualizer.getTotalSize() }}>
              {virtualRows.map((row) => {
                const item = items[row.index];
                if (!item) return null;
                return (
                  <div
                    key={row.key}
                    data-index={row.index}
                    ref={virtualizer.measureElement}
                    style={{ transform: `translateY(${row.start}px)` }}
                    className="absolute top-0 left-0 w-full"
                  >
                    <MailRow item={item} active={item.id === activeId} selected={selected.includes(item.id)} labels={labels} onClick={choose} draggable={draggableRows} onDragMailItem={onDragMailItem} highlight={highlight} />
                  </div>
                );
              })}
            </div>
            {loadingMore && (
              <p role="status" className="flex items-center justify-center gap-2 py-3 text-xs text-muted-foreground">
                <LoaderCircleIcon aria-hidden className="size-3.5 animate-spin motion-reduce:animate-none" />
                {labels.loadingMore}
              </p>
            )}
          </div>
        </ContextMenuArea>
      )}
    </section>
  );
}

interface MailRowProps {
  item: MailListItem;
  active: boolean;
  selected: boolean;
  labels: MailListLabels;
  onClick: (id: string, event: ReactMouseEvent) => void;
  draggable: boolean;
  onDragMailItem: MailListPaneProps['onDragMailItem'];
  highlight: MailHighlighter | undefined;
}

function MailRow({ item, active, selected, labels, onClick, draggable, onDragMailItem, highlight }: MailRowProps) {
  return (
    <div
      id={`mail-${item.id}`}
      role="option"
      aria-selected={selected || active}
      data-context-id={item.id}
      onClick={(event) => onClick(item.id, event)}
      draggable={draggable || undefined}
      onDragStart={
        draggable
          ? (event) => {
              event.dataTransfer.setData(MAIL_ITEM_DRAG_TYPE, JSON.stringify({ id: item.id, subject: item.subject } satisfies DraggedMailItem));
              event.dataTransfer.setData('text/plain', item.subject);
              event.dataTransfer.effectAllowed = 'copyMove';
              onDragMailItem?.({ id: item.id, subject: item.subject });
            }
          : undefined
      }
      onDragEnd={() => onDragMailItem?.(undefined)}
      className={cn(
        'relative flex cursor-pointer touch-callout-none flex-col gap-0.5 border-b px-3 py-2 text-density transition-colors select-none hover:bg-muted/60',
        (active || selected) && 'bg-accent hover:bg-accent',
        item.unread && 'before:absolute before:inset-y-0 before:left-0 before:w-0.5 before:bg-primary',
      )}
    >
      <div className="flex items-center gap-2">
        <span title={item.fromAddress} className={cn('flex-1 truncate', item.unread && 'font-semibold')}>
          {item.unread && <span className="sr-only">{labels.unread}: </span>}
          {item.from}
        </span>
        {item.hasAttachments && <PaperclipIcon aria-label={labels.hasAttachments} className="size-3.5 text-muted-foreground" />}
        {item.flagged && <FlagIcon aria-label={labels.flagged} className="size-3.5 text-destructive" />}
        <span className="shrink-0 text-xs text-muted-foreground">{item.dateLabel}</span>
      </div>
      <span className={cn('truncate', item.unread ? 'font-medium text-primary' : 'text-foreground')}>{item.subject ? (highlight ? highlight.text(item.subject, 'subject') : item.subject) : labels.noSubject}</span>
      <span className="truncate text-xs text-muted-foreground">{highlight ? highlight.text(item.preview, 'body') : item.preview}</span>
    </div>
  );
}

/* ---------------------------------------------------------------- ReadingPane */

/** A sender/recipient in the reading pane: the name is shown, the address on hover/focus. */
export interface ReadingPanePerson {
  name: string;
  /** Empty when unknown (unresolvable EX participant). */
  address: string;
  photoUrl?: string | undefined;
}

export interface ReadingPaneMessage {
  subject: string;
  from: ReadingPanePerson;
  to: readonly ReadingPanePerson[];
  cc: readonly ReadingPanePerson[];
  dateLabel: string;
  html: string;
}

export interface ReadingPaneLabels {
  emptyTitle: string;
  emptyDescription: string;
  from: string;
  to: string;
  cc: string;
  noSubject: string;
  remoteBlocked: string;
  showImages: string;
  content: string;
}

export interface ReadingPaneProps {
  message?: ReadingPaneMessage;
  loading?: boolean;
  error?: boolean;
  onRetry?: () => void;
  /** Remote images allowed for this message (per-message/per-sender choice by the app). */
  allowRemote: boolean;
  onAllowRemote: () => void;
  /** Reply / reply all / forward / delete … */
  actions?: ReactNode;
  /** Above the header, e.g. "Attached message · Back" in the nested message view. */
  banner?: ReactNode;
  /** Under the header: the AttachmentStrip. */
  attachments?: ReactNode;
  /** Address actions (copy, new mail to …): click, right-click, Shift+F10 or long-press on a person. */
  addressMenu?: (person: ReadingPanePerson) => ContextMenuSpec | undefined;
  labels: ReadingPaneLabels;
  /** Marks the matched search words in the subject and the body (applied after sanitizing). */
  highlight?: MailHighlighter | undefined;
  className?: string;
}

export function ReadingPane({ message, loading, error, onRetry, allowRemote, onAllowRemote, actions, banner, attachments, addressMenu, labels, highlight, className }: ReadingPaneProps) {
  const sanitized = useMemo(() => {
    if (!message) return undefined;
    const clean = sanitizeMailHtml(message.html, { allowRemote });
    return highlight ? { ...clean, html: highlight.html(clean.html) } : clean;
  }, [message, allowRemote, highlight]);

  if (error) return <ErrorState onRetry={onRetry} className={className} />;
  if (loading) {
    return (
      <div className={cn('flex flex-col gap-3 p-4', className)}>
        <Skeleton className="h-5 w-2/3" />
        <Skeleton className="h-3 w-1/3" />
        <Skeleton className="mt-4 h-40 w-full" />
      </div>
    );
  }
  if (!message || !sanitized) {
    return <EmptyState className={className} title={labels.emptyTitle} description={labels.emptyDescription} />;
  }

  return (
    <article className={cn('flex min-h-0 flex-col', className)}>
      {banner}
      <MessageHeader message={message} labels={labels} actions={actions} addressMenu={addressMenu} highlight={highlight} />
      {attachments}
      {sanitized.blockedRemote && !allowRemote && (
        <Alert variant="info" className="mx-4 mb-2 w-auto items-center">
          <ImageOffIcon />
          <span className="flex-1">{labels.remoteBlocked}</span>
          <Button size="sm" variant="outline" onClick={onAllowRemote}>
            {labels.showImages}
          </Button>
        </Alert>
      )}
      <MailHtmlViewer html={sanitized.html} allowRemote={allowRemote} ownColors={sanitized.ownColors} title={labels.content} />
    </article>
  );
}

export interface MessageHeaderProps {
  message: ReadingPaneMessage;
  labels: ReadingPaneLabels;
  actions?: ReactNode;
  addressMenu?: (person: ReadingPanePerson) => ContextMenuSpec | undefined;
  highlight?: MailHighlighter | undefined;
}

export function MessageHeader({ message, labels, actions, addressMenu, highlight }: MessageHeaderProps) {
  // Every person gets an index as context id: from = 0, then To, then Cc.
  const people = [message.from, ...message.to, ...message.cc];
  const contextId = (index: number) => (addressMenu ? String(index) : undefined);

  return (
    <header className="flex flex-col gap-3 border-b px-4 py-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h2 className="text-lg font-semibold break-words">{message.subject ? (highlight ? highlight.text(message.subject, 'subject') : message.subject) : labels.noSubject}</h2>
        {actions && <div className="flex items-center gap-1">{actions}</div>}
      </div>
      <ContextMenuArea menuFor={addressMenu && ((id) => addressMenu(people[Number(id)]!))} openOnClick>
        <div className="flex items-start gap-3 text-sm">
          <PersonAvatar name={message.from.name} email={message.from.address} photoUrl={message.from.photoUrl} size="lg" />
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <div className="flex flex-wrap items-baseline justify-between gap-x-2">
              <PersonName
                name={message.from.name}
                email={message.from.address}
                className="truncate font-medium"
                prefix={<span className="sr-only">{labels.from}: </span>}
                contextId={contextId(0)}
              />
              <span className="text-xs text-muted-foreground">{message.dateLabel}</span>
            </div>
            <PeopleLine label={labels.to} people={message.to} contextId={addressMenu && ((index) => String(1 + index))} />
            {message.cc.length > 0 && (
              <PeopleLine label={labels.cc} people={message.cc} contextId={addressMenu && ((index) => String(1 + message.to.length + index))} />
            )}
          </div>
        </div>
      </ContextMenuArea>
    </header>
  );
}

/** "To: Bob Test, Alice Test" with each address on hover/focus (and its actions with `contextId`). */
function PeopleLine({ label, people, contextId }: { label: string; people: readonly ReadingPanePerson[]; contextId?: (index: number) => string }) {
  return (
    <p className="line-clamp-2 text-xs text-muted-foreground">
      {label}:{' '}
      {people.map((p, index) => (
        <span key={`${p.address}-${index}`}>
          {index > 0 && ', '}
          <PersonName name={p.name} email={p.address} contextId={contextId?.(index)} />
        </span>
      ))}
    </p>
  );
}

export interface MailHtmlViewerProps {
  html: string;
  allowRemote: boolean;
  title: string;
  /** From `sanitizeMailHtml`: the mail sets its own colors and keeps its light page. */
  ownColors?: boolean;
}

/**
 * Renders already-sanitized HTML in a sandboxed iframe: no scripts, no same-origin access,
 * strict CSP; links open in a new tab. Mails without own colors use the themed app surface;
 * mails with own colors are shown as a framed light page, also in dark mode.
 */
export function MailHtmlViewer({ html, allowRemote, title, ownColors = false }: MailHtmlViewerProps) {
  const theme = useMailTheme();
  const doc = useMemo(() => buildMailDocument(html, { allowRemote, theme, ownColors }), [html, allowRemote, theme, ownColors]);
  const paper = ownColors && theme?.colorScheme === 'dark';
  return (
    <iframe
      title={title}
      sandbox="allow-popups allow-popups-to-escape-sandbox"
      referrerPolicy="no-referrer"
      srcDoc={doc}
      className={cn('min-h-0 flex-1', paper ? 'm-4 mt-2 self-stretch rounded-md border' : 'w-full border-0 bg-background')}
    />
  );
}
